import {
  HistoryMessageItem,
  isTextMessage,
  MessageCallReceived,
  MessageCallRecordCompleted,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  useIM,
} from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, type Ref } from 'vue'

import { chatModel } from '@/entities/messages'
import { useChatFilesMarkersStore } from '@/entities/room'

import sort from '@/shared/utils/sort'
import { pServedIsRoom } from '@/shared/utils/uri'

import { GALLERY_HISTORY_BATCH_LIMIT } from './constants'

type GalleryMessage =
  | HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  | HistoryMessageItem<MessageCallRecordCompleted>

const isCallRecordCompletedMessage = (
  message: HistoryMessageItem,
): message is HistoryMessageItem<MessageCallRecordCompleted> => {
  return (message.item as MessageCallReceived)?.event === 'call_record_completed'
}

const isGalleryHistoryMessage = (message: HistoryMessageItem): message is GalleryMessage => {
  return isTextMessage(message) || isCallRecordCompletedMessage(message)
}

const resolveMinRank = (messages: HistoryMessageItem[]) => {
  return messages.reduce((minRank, event) => {
    if (event.rank === undefined) {
      return minRank
    }
    return Math.min(minRank, event.rank)
  }, Number.POSITIVE_INFINITY)
}

const resolveMaxRank = (messages: HistoryMessageItem[]) => {
  return messages.reduce((maxRank, event) => {
    if (event.rank === undefined) {
      return maxRank
    }
    return Math.max(maxRank, event.rank)
  }, 0)
}

type RequestOlderHistoryParams = {
  pServedValue: string
  messagesLimit: number
  cursorEventId?: number
}

export const useChatGalleryHistory = (pServed: Ref<string>) => {
  const { requestChatHistory } = useIM()
  const { findRoomMessages } = chatModel.useChatMessageStore()
  const { filesMarkersByPServedMap } = storeToRefs(useChatFilesMarkersStore())

  const galleryMessages = ref<GalleryMessage[]>([])
  const isLoading = ref(false)
  /**
   * Like search `lastFetchedRank`: how far back we still need to walk.
   * History is exhausted when this reaches 0 (same model as chat message search).
   */
  const lastFetchedRank = ref(0)
  /** Cursor for the next older-history request; advanced from response when possible. */
  const olderHistoryCursorEventId = ref<number | undefined>(undefined)

  const roomMessages = computed(() => findRoomMessages(pServed.value))

  const allMessages = computed(() => {
    const uniqueMessages = new Map<number, GalleryMessage>()

    ;[...roomMessages.value, ...galleryMessages.value].forEach((message) => {
      const eventId = message.eventId
      if (eventId && !uniqueMessages.has(eventId)) {
        uniqueMessages.set(eventId, message as GalleryMessage)
      }
    })

    return [...uniqueMessages.values()]
  })

  const markerFilesCount = computed(() => {
    if (!pServed.value) {
      return 0
    }

    return filesMarkersByPServedMap.value.get(pServed.value)?.count ?? 0
  })

  const loadedFilesCount = computed(() => {
    return allMessages.value
      .filter(isTextMessage)
      .reduce((count, message) => count + (message.item.files?.length ?? 0), 0)
  })

  /** Ещё есть история для обхода (не дошли до rank 0) — как в поиске */
  const hasMoreHistory = computed(() => lastFetchedRank.value > 0)

  const canLoadMoreHistory = computed(() => hasMoreHistory.value && !isLoading.value)

  const hasPendingFiles = computed(() => {
    return markerFilesCount.value > loadedFilesCount.value
  })

  const getOldestKnownEventId = () => {
    if (olderHistoryCursorEventId.value !== undefined) {
      return olderHistoryCursorEventId.value
    }

    if (galleryMessages.value.length) {
      return galleryMessages.value
        .slice()
        .sort(sort.chatEvents.byTimestamp)
        .at(-1)?.eventId
    }

    return roomMessages.value.at(0)?.eventId
  }

  const advanceHistoryCursor = (requestRank: number) => {
    lastFetchedRank.value = Math.max(0, requestRank - GALLERY_HISTORY_BATCH_LIMIT)
  }

  const reset = () => {
    galleryMessages.value = []
    isLoading.value = false
    lastFetchedRank.value = 0
    olderHistoryCursorEventId.value = undefined
  }

  const appendGalleryEvents = (events: GalleryMessage[]) => {
    if (!events.length) {
      return 0
    }

    const existingIds = new Set([
      ...roomMessages.value.map(({ eventId }) => eventId),
      ...galleryMessages.value.map(({ eventId }) => eventId),
    ])

    const newEvents = events.filter(({ eventId }) => eventId && !existingIds.has(eventId))
    if (newEvents.length) {
      galleryMessages.value = [...galleryMessages.value, ...newEvents]
    }

    return newEvents.length
  }

  const requestOlderHistory = async (params: RequestOlderHistoryParams) => {
    const requestPayload = {
      pServed: params.pServedValue,
      isConference: pServedIsRoom(params.pServedValue),
      messagesLimit: params.messagesLimit,
      type: 'older',
    } as const

    if (params.cursorEventId === undefined) {
      return requestChatHistory(requestPayload)
    }

    return requestChatHistory({
      ...requestPayload,
      cursorEventId: params.cursorEventId,
    })
  }

  const fetchOlderBatch = async () => {
    const pServedValue = pServed.value
    if (!pServedValue) {
      return
    }

    const isBootstrap = lastFetchedRank.value <= 0
      && olderHistoryCursorEventId.value === undefined
      && roomMessages.value.length === 0
      && galleryMessages.value.length === 0

    // Rank exhausted but markers still say there are files — keep walking by cursor.
    const pendingOnly = !isBootstrap
      && lastFetchedRank.value <= 0
      && hasPendingFiles.value

    if (!isBootstrap && lastFetchedRank.value <= 0 && !hasPendingFiles.value) {
      return
    }

    if (pendingOnly && getOldestKnownEventId() === undefined) {
      return
    }

    const requestRank = lastFetchedRank.value
    const cursorEventId = getOldestKnownEventId()
    const { events } = cursorEventId !== undefined
      ? await requestOlderHistory({
        pServedValue,
        cursorEventId,
        messagesLimit: GALLERY_HISTORY_BATCH_LIMIT,
      })
      : await requestOlderHistory({
        pServedValue,
        messagesLimit: GALLERY_HISTORY_BATCH_LIMIT,
      })

    if (pServedValue !== pServed.value) {
      return
    }

    const batch = events ?? []
    const eventsToAppend = batch.filter(isGalleryHistoryMessage)
    appendGalleryEvents(eventsToAppend)

    if (batch.length) {
      const eventsSorted = batch.slice().sort(sort.chatEvents.byTimestamp)
      const nextCursor = eventsSorted.at(-1)?.eventId
      if (nextCursor !== undefined) {
        olderHistoryCursorEventId.value = nextCursor
      }
    }

    // Pending-files walk: only advance the event cursor, keep rank at 0.
    if (pendingOnly) {
      return
    }

    // Same as search: always move the rank window by messagesLimit, ignore empty/short pages.
    const rankToAdvance = requestRank > 0
      ? requestRank
      : resolveMaxRank(batch)

    if (rankToAdvance > 0) {
      advanceHistoryCursor(rankToAdvance)
      return
    }

    lastFetchedRank.value = 0
  }

  const loadOlder = async () => {
    const isBootstrap = lastFetchedRank.value <= 0
      && olderHistoryCursorEventId.value === undefined
      && roomMessages.value.length === 0
      && galleryMessages.value.length === 0

    if (
      isLoading.value
      || (!hasMoreHistory.value && !hasPendingFiles.value && !isBootstrap)
    ) {
      return
    }

    isLoading.value = true
    try {
      await fetchOlderBatch()
    } catch {
      console.error('Error loading chat gallery history')
    } finally {
      isLoading.value = false
    }
  }

  const syncRankCursorFromRoom = () => {
    const minRank = resolveMinRank(roomMessages.value)
    lastFetchedRank.value = Number.isFinite(minRank) ? minRank : 0
    olderHistoryCursorEventId.value = roomMessages.value.at(0)?.eventId
  }

  const loadInitial = async () => {
    if (roomMessages.value.length > 0) {
      syncRankCursorFromRoom()
      return
    }

    await loadOlder()
  }

  watch(pServed, () => {
    reset()
    void loadInitial()
  }, { immediate: true })

  return {
    allMessages,
    canLoadMoreHistory,
    galleryMessages,
    hasMoreHistory,
    hasPendingFiles,
    isLoading,
    lastFetchedRank,
    loadedFilesCount,
    loadInitial,
    loadOlder,
    markerFilesCount,
    reset,
  }
}
